import { useEffect, useMemo } from "react";

export const SITE_URL = process.env.REACT_APP_SITE_URL;
export const SITE_NAME = "Chai Times";
export const DEFAULT_IMAGE = "/kulhads-many.webp";

export const absoluteUrl = (path = "/") => new URL(path, SITE_URL).toString();

export const absoluteAsset = (path = DEFAULT_IMAGE) => {
  if (!path) path = DEFAULT_IMAGE;
  if (path.startsWith("http")) return path;
  if (path.startsWith("/")) return absoluteUrl(path);
  return absoluteUrl(`/api/files/${path}`);
};

export const organizationSchema = {
  "@type": "Organization",
  "@id": absoluteUrl("/#organization"),
  name: SITE_NAME,
  alternateName: "chaitimes.in",
  url: absoluteUrl("/"),
  logo: {
    "@type": "ImageObject",
    url: absoluteAsset("/logo.png"),
  },
  description: "Kulhad chai café brand serving kadak chai, coffee, snacks and adda culture across Bengaluru.",
  email: "info@chaitimes.in",
  telephone: "+91-7019368165",
  address: {
    "@type": "PostalAddress",
    streetAddress: "No. 415, Ground Floor, 4th B Cross, HRBR Layout, Kalyan Nagar",
    addressLocality: "Bengaluru",
    addressRegion: "Karnataka",
    postalCode: "560043",
    addressCountry: "IN",
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+91-7019368165",
    email: "info@chaitimes.in",
    contactType: "customer service",
    areaServed: "IN",
  },
  sameAs: ["https://www.instagram.com/chaitimes.in"],
};

export const websiteSchema = {
  "@type": "WebSite",
  "@id": absoluteUrl("/#website"),
  url: absoluteUrl("/"),
  name: SITE_NAME,
  alternateName: "chaitimes.in",
  publisher: { "@id": organizationSchema["@id"] },
  inLanguage: "en-IN",
};

export const breadcrumbSchema = (items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.path),
  })),
});

export const webPageSchema = ({ type = "WebPage", name, description, path }) => ({
  "@type": type,
  "@id": absoluteUrl(`${path}#webpage`),
  url: absoluteUrl(path),
  name,
  description,
  isPartOf: { "@id": websiteSchema["@id"] },
  publisher: { "@id": organizationSchema["@id"] },
  inLanguage: "en-IN",
});

export const localBusinessSchema = (location) => ({
  "@type": "CafeOrCoffeeShop",
  "@id": absoluteUrl(`/locations#${location.id}`),
  name: location.name,
  image: absoluteAsset("/outlet-interior.webp"),
  url: absoluteUrl("/locations"),
  parentOrganization: { "@id": organizationSchema["@id"] },
  address: {
    "@type": "PostalAddress",
    streetAddress: location.address,
    addressLocality: location.city || "Bengaluru",
    addressRegion: "Karnataka",
    addressCountry: "IN",
  },
  areaServed: location.area,
  openingHours: "Mo-Su 08:00-24:00",
  priceRange: "₹",
  servesCuisine: ["Chai", "Tea", "Coffee", "Café Snacks"],
});

const setMeta = (attribute, name, content) => {
  let tag = document.head.querySelector(`meta[${attribute}="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const setCanonical = (url) => {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", url);
};

const Seo = ({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  type = "website",
  noindex = false,
  schema = [],
}) => {
  const schemaNodes = useMemo(
    () => (Array.isArray(schema) ? schema.filter(Boolean) : [schema].filter(Boolean)),
    [schema]
  );

  useEffect(() => {
    const url = absoluteUrl(path);
    const imageUrl = absoluteAsset(image);
    const robots = noindex
      ? "noindex, nofollow"
      : "index, follow, max-image-preview:large, max-snippet:-1";

    document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "robots", robots);
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:type", type);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", imageUrl);
    setMeta("property", "og:image:alt", `${SITE_NAME} — ${title}`);
    setMeta("property", "og:locale", "en_IN");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", imageUrl);
    setCanonical(url);

    document.head.querySelectorAll('script[data-route-schema="true"]').forEach((node) => node.remove());
    let script = null;
    if (schemaNodes.length) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.routeSchema = "true";
      script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": schemaNodes });
      document.head.appendChild(script);
    }

    return () => {
      if (script) script.remove();
    };
  }, [title, description, path, image, type, noindex, schemaNodes]);

  return null;
};

export default Seo;
